import { LeagueClassicPage } from './LeagueClassicPage.js';
import { LeagueMediaMini }   from './LeagueMediaMini.js';
/**
 * LeagueSAFF.js &#8212; Saudi Women's Premier League
 * Architecture mirrors LeagueNWSL.js &#8212; responsive, CSS-injected.
 */
import {
  SAFF_STANDINGS_FALLBACK,
  SAFF_TOP_SCORERS_FALLBACK,
  SAFF_FIXTURES_FALLBACK,
  SAFF_SEASON,
  fetchLiveSAFFData,
} from '../../data/saffData.js';
import { injectCSS, gdHtml, formPills, fmtDate, BASE_CSS } from '../../leagueBase.js';

const STYLE_ID = 'leaguesaff-styles';
const ACCENT   = '#008000';   // Saudi green

function inject() {
  injectCSS(STYLE_ID, BASE_CSS + `
    .leaguesaff-tab.active { border-bottom-color: ${ACCENT}; }
    .leaguesaff-badge      { background: ${ACCENT}; color:#fff; }
    .leaguesaff-scorer-g   { color: ${ACCENT}; }
    .leaguesaff-pts        { color: ${ACCENT}; font-weight:700; }
  `);
}

// &#9472;&#9472; HTML helpers &#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;
function fixtureSidebar(fixtures = []) {
  const shown = [
    ...fixtures.filter(f => f.status === 'FT').slice(-3),
    ...fixtures.filter(f => f.status === 'NS').slice(0, 3),
  ];
  return shown.map(f => `
    <div class="lg-fix">
      <div class="lg-fix-date">${fmtDate(f.date)}</div>
      <div class="lg-fix-teams">
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.homeColor||ACCENT}"></div><span class="lg-fix-name">${f.home}</span></div>
        <div class="lg-fix-team"><div class="lg-dot" style="background:${f.awayColor||'#999'}"></div><span class="lg-fix-name">${f.away}</span></div>
      </div>
      <div class="lg-fix-score ${f.status==='NS'?'upcoming':''}">${f.status==='NS'?'vs':`${f.homeGoals}&#8211;${f.awayGoals}`}</div>
    </div>`).join('');
}

function scorerSidebar(scorers = []) {
  return scorers.slice(0, 6).map((s, i) => `
    <div class="lg-scorer">
      <div class="lg-scorer-rank">${i+1}</div>
      <div class="lg-av">${s.initials}</div>
      <div class="lg-scorer-info">
        <div class="lg-scorer-name">${s.name}</div>
        <div class="lg-scorer-club">${s.club}</div>
      </div>
      <div class="lg-scorer-g">${s.goals}</div>
    </div>`).join('');
}

function standingsRows(rows = []) {
  return rows.map(t => `
    <tr class="lg-zone-${t.zone||'n'}">
      <td>${t.pos}</td>
      <td class="l"><div class="lg-team-cell"><div class="lg-dot" style="background:${t.color}"></div><span class="lg-name">${t.name}</span></div></td>
      <td>${t.p}</td>
      <td class="lg-col-hide">${t.w}</td>
      <td class="lg-col-hide">${t.d}</td>
      <td class="lg-col-hide">${t.l}</td>
      <td class="lg-col-hide">${gdHtml(t.gd)}</td>
      <td class="lg-pts">${t.pts}</td>
      <td class="lg-col-hide"><div class="lg-form">${formPills(t.form||[])}</div></td>
    </tr>`).join('');
}

// &#9472;&#9472; Main class &#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;
export class LeagueSAFF {
  constructor(containerId) {
    this.containerId = containerId;
    this.root        = null;
    this.data = {
      standings: SAFF_STANDINGS_FALLBACK,
      scorers:   SAFF_TOP_SCORERS_FALLBACK,
      fixtures:  SAFF_FIXTURES_FALLBACK,
    };
  }

  async init() {
    inject();
    this.root = document.getElementById(this.containerId);
    if (!this.root) return;
    const live = await fetchLiveSAFFData();
    if (live) {
      if (live.standings)  this.data.standings = live.standings;
      if (live.topScorers) this.data.scorers   = live.topScorers;
      if (live.fixtures)   this.data.fixtures  = live.fixtures;
    }
    this.render();
  }

  render() {
    this.root.innerHTML = this._html();
    this._attachListeners();
  }

  _html() {
    const { standings, scorers, fixtures } = this.data;
    return `
    <div class="lg-wrap">
      <div class="lg-hero">
        <div class="lg-logo-box" style="background:transparent;"><img src="/images/league_saff.png" style="width:40px;height:40px;object-fit:contain;display:block;"></div>
        <div class="lg-hero-text">
          <div class="lg-hero-title">Saudi Women's Premier League</div>
          <div class="lg-hero-sub">${SAFF_SEASON} &#183; 8 Clubs &#183; Al-Nassr defending champions</div>
        </div>
      </div>

      <div class="lg-tabs" role="tablist">
        <button class="lg-tab leaguesaff-tab active" data-tab="standings">Table</button>
        <button class="lg-tab leaguesaff-tab" data-tab="scorers">Scorers</button>
        <button class="lg-tab leaguesaff-tab" data-tab="clubs">Clubs</button>
        <button class="lg-tab leaguesaff-tab" data-tab="media">Media</button>
        <button class="lg-tab leaguesaff-tab" data-tab="classic">Classic</button>
        <button class="lg-tab leaguesaff-tab" data-tab="about">About</button>
      </div>

      <div class="lg-body">
        <div class="lg-main">
          <div data-panel="standings">
            <table class="lg-table">
              <thead><tr>
                <th>#</th><th class="l">Club</th><th>P</th>
                <th class="lg-col-hide">W</th><th class="lg-col-hide">D</th><th class="lg-col-hide">L</th>
                <th class="lg-col-hide">GD</th><th>Pts</th><th class="lg-col-hide">Form</th>
              </tr></thead>
              <tbody>${standingsRows(standings)}</tbody>
            </table>
            <div class="lg-legend">
              <span class="lg-legend-dot champion"></span>Champions
              &nbsp;&#183;&nbsp;<span class="lg-legend-dot rel"></span>Relegation
            </div>
          </div>
          <div data-panel="scorers" style="display:none">
            <div class="lg-scorer-list">${scorerSidebar(scorers)}</div>
          </div>
          <div data-panel="fixtures" style="display:none">
            <div class="lg-fix-list">${fixtureSidebar(fixtures)}</div>
          </div>
          <div data-panel="clubs" style="display:none"><div style="padding:12px 0;"><div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;">${(standings||[]).map(t=>`<div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:10px;padding:14px 10px;text-align:center;"><div style="width:44px;height:44px;border-radius:50%;background:${t.color||'#888'};display:flex;align-items:center;justify-content:center;margin:0 auto 8px;font-size:12px;font-weight:700;color:#fff;">${t.short||'?'}</div><div style="font-size:11px;font-weight:600;color:var(--text-color,#111);line-height:1.3;margin-bottom:4px;">${t.name||''}</div><div style="font-size:10px;color:var(--text-secondary,#888);">#${t.pos||''} &middot; ${t.pts||0} pts</div></div>`).join('')}</div></div></div>
          <div data-panel="media" style="display:none"><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Media - Coming Soon</div></div></div>
          <div data-panel="classic" style="display:none">
            <div class="wsl-panel" id="saff-panel-media"></div>
            <div class="wsl-panel" id="saff-panel-classic"></div><div style="padding:40px 20px;text-align:center;color:var(--text-secondary,#888);"><div style="font-size:15px;font-weight:600;">Classic - Coming Soon</div></div></div>

          <div data-panel="about" style="display:none"><div style="padding:14px;max-width:900px;"><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; Overview</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">The Saudi Women&#39;s Premier League, governed by the Saudi Arabian Football Federation. Established as part of Vision 2030, marking a historic transformation for women&#39;s sport in the Kingdom.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; History</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Launched in 2021, the league represents one of the most significant changes in Saudi Arabia&#39;s sporting landscape. Women&#39;s football had not been officially recognised before Vision 2030 reforms.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; Honours</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">The league is building its competitive history. <strong>Al-Nassr Ladies</strong> and <strong>Al-Ittihad Ladies</strong> have featured prominently in early seasons of this groundbreaking competition.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; Stars</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">The league focuses on developing Saudi women&#39;s talent for the national team and future participation in AFC competitions, with the federation investing in academies and coaching.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; TV &amp; Media</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">Broadcast on <strong>SSC Sport</strong> and Saudi federation digital channels. Coverage is expanding as part of Saudi Arabia&#39;s broader sports media investment under Vision 2030.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; Asia</h4><div style="font-size:12px;color:var(--text-secondary,#555);line-height:1.7;">As an AFC member, Saudi Arabia&#39;s women&#39;s teams compete in Asian qualifying. The league aims to build a squad capable of reaching the Asian Cup and eventually World Cup qualification.</div></div><div style="background:var(--card-bg,#fff);border:1px solid var(--border-color,#eee);border-radius:12px;padding:14px 16px;margin-bottom:12px;"><h4 style="margin:0 0 8px;font-size:13px;font-weight:700;color:#006600;">&#9632; Key Facts</h4><div><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">SAFF governed</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Founded 2021</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">Vision 2030</span><span style="display:inline-block;background:var(--hover-bg,#f5f5f5);border-radius:6px;padding:3px 8px;margin:2px;font-size:11px;font-weight:600;">AFC member</span></div></div></div></div>
        </div>
      </div>
    </div>`;
  }

  _attachListeners() {
    this.root.querySelectorAll('.lg-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('.lg-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active')
        const _tt=btn.dataset.tab||'';if(_tt==='classic'){const _ce=this.root&&this.root.querySelector('#saff-panel-classic');if(_ce&&!_ce._cpm){new LeagueClassicPage(_ce,'saff').init();_ce._cpm=true;}}if(_tt==='media'){const _me=this.root&&this.root.querySelector('#saff-panel-media');if(_me&&!_me._mmm){new LeagueMediaMini(_me,'saff').init();_me._mmm=true;}};
        const tab = btn.dataset.tab;
        this.root.querySelectorAll('[data-panel]').forEach(p => {
          p.style.display = p.dataset.panel === tab ? '' : 'none';
        });
      });
    });
  }
}
